<template>
  <div class="active-account-report">
    <el-card>
      <div slot="header" class="clearfix no-print">
        <span>Active Account</span>
      </div>
      <div>
        <div class="search-area no-print">
          <el-select v-model="year" placeholder="Year" style="width:100px" @change="getTableData()">
            <el-option v-for="item in yearOptions" :key="item.value" :label="item.value" :value="item.value"></el-option>
          </el-select>
          <el-button
            slot="append"
            type="primary"
            icon="el-icon-refresh"
            @click="getTableData()"
            :loading="loading"
            >Query</el-button
          >
          <el-button
            type="danger"
            icon="el-icon-printer"
            :disabled="!reportData"
            @click="printReport()"
            >Print PDF</el-button
          >
        </div>

        <div v-loading="loading" ref="reportBody" class="report-body">
          <template v-if="reportData">
            <div class="report-year"><h1>Active Account {{ reportData.year }}</h1></div>

            <el-table :data="summaryRows" border style="width: 100%" class="account-report-table">
              <el-table-column
                prop="label"
                label=""
                width="160"
                header-align="center"
                align="center"
                class-name="metric-column"
                label-class-name="metric-column"
              />
              <el-table-column
                v-for="(month, index) in monthLabels"
                :key="month"
                :label="month"
                header-align="center"
                align="center"
                min-width="70"
              >
                <template slot-scope="scope">
                  {{ scope.row.values[index] }}
                </template>
              </el-table-column>
            </el-table>

            <div ref="chart" class="chart-container"></div>

            <div class="diff-title">Month-over-month difference</div>
            <el-table :data="diffRows" border style="width: 100%" class="account-report-table">
              <el-table-column
                prop="label"
                label="Metric"
                width="160"
                header-align="center"
                align="center"
                class-name="metric-column"
                label-class-name="metric-column"
              />
              <el-table-column
                v-for="col in diffColumns"
                :key="col.key"
                :label="col.label"
                header-align="center"
                align="center"
                min-width="90"
              >
                <template slot-scope="scope">
                  {{ scope.row.values[col.key] }}
                </template>
              </el-table-column>
            </el-table>
          </template>
          <el-empty v-else-if="!loading" description="No data"></el-empty>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import echarts from "echarts";
import { getActiveAccount } from "@/api/report";
import { currentYear } from "@/utils";

const MONTH_LABELS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

const CHART_COLORS = {
  active: "#409EFF",
  closed: "#E6A23C",
  created: "#67C23A",
};

function countsByMonth(series) {
  const map = {};
  (series || []).forEach(({ month, count }) => {
    const monthIndex = parseInt(month.split("-")[1], 10) - 1;
    map[monthIndex] = count;
  });
  return MONTH_LABELS.map((_, index) => (map[index] !== undefined ? map[index] : 0));
}

function formatDiff(current, previous) {
  const diff = current - previous;
  if (diff > 0) return `+${diff}`;
  return String(diff);
}

function chartYAxisMax(maxValue) {
  if (maxValue <= 0) return 5;
  return Math.ceil(maxValue * 1.05);
}

const PRINT_CHART_WIDTH = 1050;
const PRINT_CHART_HEIGHT = 300;

function createPrintTable(headers, bodyRows) {
  const table = document.createElement("table");
  table.className = "print-native-table";

  const thead = document.createElement("thead");
  const headerRow = document.createElement("tr");
  headers.forEach((header) => {
    const th = document.createElement("th");
    th.textContent = header;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  bodyRows.forEach((cells) => {
    const row = document.createElement("tr");
    cells.forEach((text, index) => {
      const cell = document.createElement("td");
      if (index === 0) {
        cell.className = "metric-column";
      }
      cell.textContent = text;
      row.appendChild(cell);
    });
    tbody.appendChild(row);
  });
  table.appendChild(tbody);

  return table;
}

export default {
  name: "ActiveAccountReport",
  props: {
    project: {
      type: String,
      required: true,
      default: "",
    },
  },
  watch: {
    project: {
      handler(newVal) {
        if (newVal) this.getTableData();
      },
      immediate: true,
    },
  },
  data() {
    return {
      year: currentYear(),
      reportData: null,
      yearOptions: [],
      loading: false,
      chart: null,
      monthLabels: MONTH_LABELS,
    };
  },
  computed: {
    activeCounts() {
      return countsByMonth(this.reportData && this.reportData.activeAccounts);
    },
    closedCounts() {
      return countsByMonth(this.reportData && this.reportData.closedAccounts);
    },
    createdCounts() {
      return countsByMonth(this.reportData && this.reportData.createdAccounts);
    },
    summaryRows() {
      return [
        { label: "active account", values: this.activeCounts },
        { label: "closed account", values: this.closedCounts },
        { label: "created account", values: this.createdCounts },
      ];
    },
    diffColumns() {
      return MONTH_LABELS.slice(0, -1).map((month, index) => ({
        key: `${month}-${MONTH_LABELS[index + 1]}`,
        label: `${month}->${MONTH_LABELS[index + 1]}`,
        from: index,
        to: index + 1,
      }));
    },
    diffRows() {
      return this.summaryRows.map((row) => {
        const values = {};
        this.diffColumns.forEach((col) => {
          values[col.key] = formatDiff(row.values[col.to], row.values[col.from]);
        });
        return { label: row.label, values };
      });
    },
  },
  methods: {
    getTableData() {
      if (!this.project) {
        this.reportData = null;
        this.clearChart();
        return;
      }
      this.loading = true;
      getActiveAccount(this.project, this.year)
        .then(({ result, data }) => {
          if (result) {
            this.reportData = data;
            this.$nextTick(() => this.renderChart());
          } else {
            this.reportData = null;
            this.clearChart();
          }
        })
        .catch((error) => {
          this.reportData = null;
          this.clearChart();
          this.$message.error(error.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    dropdownYear() {
      for (let i = 2018; i <= currentYear(); i++) {
        this.yearOptions.push({ value: i.toString() });
      }
    },
    buildChartOption(animation = true) {
      const allValues = [...this.activeCounts, ...this.closedCounts, ...this.createdCounts];
      const maxValue = Math.max(...allValues, 0);
      const yMax = chartYAxisMax(maxValue);

      return {
        animation,
        title: {
          text: `Monthly Account Comparison (${this.reportData.year})`,
          left: "center",
        },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: ["active account", "closed account", "created account"],
          bottom: 0,
        },
        grid: {
          left: "8%",
          right: "4%",
          bottom: "15%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          name: "Month",
          nameLocation: "middle",
          nameGap: 30,
          data: MONTH_LABELS,
        },
        yAxis: {
          type: "value",
          name: "Number of Accounts",
          nameLocation: "middle",
          nameRotate: 90,
          nameGap: 55,
          min: 0,
          max: yMax,
          minInterval: 1,
          splitNumber: 5,
          axisLabel: {
            formatter(value) {
              return Number.isInteger(value) ? value : "";
            },
          },
        },
        series: [
          {
            name: "active account",
            type: "bar",
            data: this.activeCounts,
            itemStyle: { color: CHART_COLORS.active },
          },
          {
            name: "closed account",
            type: "bar",
            data: this.closedCounts,
            itemStyle: { color: CHART_COLORS.closed },
          },
          {
            name: "created account",
            type: "bar",
            data: this.createdCounts,
            itemStyle: { color: CHART_COLORS.created },
          },
        ],
      };
    },
    renderChart() {
      if (!this.$refs.chart || !this.reportData) return;

      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chart);
      }

      this.chart.setOption(this.buildChartOption());
    },
    clearChart() {
      if (this.chart) {
        this.chart.clear();
      }
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    getChartImageForPrint() {
      return new Promise((resolve) => {
        if (!this.reportData) {
          resolve("");
          return;
        }

        const tempDiv = document.createElement("div");
        tempDiv.style.cssText = `position:fixed;left:-10000px;top:0;width:${PRINT_CHART_WIDTH}px;height:${PRINT_CHART_HEIGHT}px;`;
        document.body.appendChild(tempDiv);

        const tempChart = echarts.init(tempDiv);
        tempChart.setOption(this.buildChartOption(false), true);
        tempChart.resize({ width: PRINT_CHART_WIDTH, height: PRINT_CHART_HEIGHT });

        let captured = false;
        const capture = () => {
          if (captured) return;
          captured = true;

          const dataUrl = tempChart.getDataURL({
            type: "png",
            pixelRatio: 2,
            backgroundColor: "#fff",
          });

          tempChart.dispose();
          if (tempDiv.parentNode) {
            tempDiv.parentNode.removeChild(tempDiv);
          }
          resolve(dataUrl);
        };

        tempChart.on("finished", capture);
        setTimeout(capture, 300);
      });
    },
    buildPrintContent() {
      const wrapper = document.createElement("div");
      wrapper.className = "report-body print-compact";

      const titleEl = document.createElement("div");
      titleEl.className = "print-report-title";
      titleEl.textContent = `Active Account ${this.reportData.year}`;
      wrapper.appendChild(titleEl);

      const summaryHeaders = ["", ...MONTH_LABELS];
      const summaryBody = this.summaryRows.map((row) => [row.label, ...row.values]);
      const summaryTable = createPrintTable(summaryHeaders, summaryBody);
      summaryTable.classList.add("print-summary-table");
      wrapper.appendChild(summaryTable);

      const chartSlot = document.createElement("div");
      chartSlot.className = "chart-print-slot";
      wrapper.appendChild(chartSlot);

      const diffTitle = document.createElement("div");
      diffTitle.className = "diff-title";
      diffTitle.textContent = "Month-over-month difference";
      wrapper.appendChild(diffTitle);

      const diffHeaders = ["Metric", ...this.diffColumns.map((col) => col.label)];
      const diffBody = this.diffRows.map((row) => [
        row.label,
        ...this.diffColumns.map((col) => row.values[col.key]),
      ]);
      wrapper.appendChild(createPrintTable(diffHeaders, diffBody));

      return wrapper;
    },
    printReport() {
      if (!this.reportData) {
        this.$message.warning("No data to print");
        return;
      }

      this.getChartImageForPrint().then((chartDataUrl) => {
        const printRoot = document.createElement("div");
        printRoot.id = "active-account-print-root";
        const reportContent = this.buildPrintContent();
        const originalTitle = document.title;
        const printFilename = `Active Account ${this.reportData.year}.pdf`;

        const cleanup = () => {
          document.title = originalTitle;
          document.body.classList.remove("active-account-printing");
          if (printRoot.parentNode) {
            printRoot.parentNode.removeChild(printRoot);
          }
          window.removeEventListener("afterprint", cleanup);
          this.$nextTick(() => {
            if (this.chart) {
              this.chart.resize();
            }
          });
        };

        const startPrint = () => {
          document.title = printFilename;
          document.body.classList.add("active-account-printing");
          window.addEventListener("afterprint", cleanup);
          window.print();
        };

        const chartSlot = reportContent.querySelector(".chart-print-slot");
        if (chartSlot && chartDataUrl) {
          const chartImg = document.createElement("img");
          chartImg.className = "chart-print-image";
          chartImg.alt = `Monthly Account Comparison (${this.reportData.year})`;
          chartSlot.appendChild(chartImg);

          printRoot.appendChild(reportContent);
          document.body.appendChild(printRoot);

          let printed = false;
          const printWhenReady = () => {
            if (printed) return;
            printed = true;
            startPrint();
          };

          chartImg.onload = printWhenReady;
          chartImg.onerror = printWhenReady;
          chartImg.src = chartDataUrl;
          if (chartImg.complete) {
            printWhenReady();
          }
          return;
        }

        if (chartSlot) {
          chartSlot.parentNode.removeChild(chartSlot);
        }

        printRoot.appendChild(reportContent);
        document.body.appendChild(printRoot);
        startPrint();
      });
    },
  },
  mounted() {
    if (this.project) this.getTableData();
    this.dropdownYear();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
};
</script>

<style lang="scss">
.active-account-report {
  .search-area {
    margin-bottom: 16px;
    button {
      margin-left: 10px;
      margin-bottom: 16px;
    }
    .el-date-editor,
    .el-select {
      margin-left: 10px;
      margin-bottom: 16px;
    }
  }

  .report-body {
    min-height: 120px;
  }

  .report-year {
    text-align: center;
    margin: 8px 0 16px;
  }

  .el-table {
    margin-bottom: 16px;
  }

  .account-report-table {
    .metric-column {
      background-color: #f5f7fa !important;
    }
  }

  .chart-container {
    width: 100%;
    height: 420px;
    margin-bottom: 16px;
  }

  .diff-title {
    margin: 0 0 12px;
  }
}

@media print {
  @page {
    margin: 5mm;
    padding: 5mm;
    padding-top: 10mm;
    size: landscape;
  }

  body.active-account-printing > *:not(#active-account-print-root) {
    display: none !important;
  }

  body.active-account-printing #active-account-print-root {
    display: block !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  body.active-account-printing #active-account-print-root .report-body {
    width: 100% !important;
    min-height: auto !important;
    padding: 0 !important;
  }

  body.active-account-printing #active-account-print-root .print-report-title {
    margin: 0 0 15mm;
    font-size: 28px;
    font-weight: bold;
    text-align: center;
  }

  body.active-account-printing #active-account-print-root .print-summary-table {
    margin-bottom: 24px;
  }

  body.active-account-printing #active-account-print-root .chart-print-slot {
    margin: 24px 0;
  }

  body.active-account-printing #active-account-print-root .print-native-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin-bottom: 8px;
    font-size: 8px;

    th,
    td {
      border: 1px solid #ebeef5;
      padding: 2px 1px;
      text-align: center;
      vertical-align: middle;
      word-break: break-word;
      line-height: 1.15;
    }

    th {
      background-color: #f5f7fa;
      font-weight: bold;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .metric-column {
      background-color: #f5f7fa !important;
      font-weight: bold;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  body.active-account-printing #active-account-print-root .chart-print-image {
    display: block !important;
    width: 100% !important;
    height: auto !important;
    margin-bottom: 15mm;
    margin-top: 15mm;
    page-break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body.active-account-printing #active-account-print-root .diff-title {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: bold;
  }
}
</style>
